// OpenAI configuration
export const OPENAI_CONFIG = {
    model: 'gpt-4',
    temperature: 0.7,
    presence_penalty: 0.1,
    frequency_penalty: 0.1
};

// Message window configuration
const MAX_MESSAGES = 10; // Keep last 10 messages for context

// Function to get last N messages
function getLastMessages(messages) {
    return messages.slice(-MAX_MESSAGES);
}

export const streamOpenAIResponse = async function*(promptToUse, messages, config) {
    const { apiKey, useProxy, user } = config;

    // Force proxy mode if VITE_OPENAI_API_KEY is 'PROXY'
    const shouldUseProxy = useProxy ||
        import.meta.env.VITE_OPENAI_API_KEY === 'PROXY';

    const endpoint = shouldUseProxy ?
        `${import.meta.env.VITE_WORKER_URL}/api/chat` :
        'https://api.openai.com/v1/chat/completions';

    // Get last N messages for context
    const contextMessages = getLastMessages(messages);
    console.log(`Using ${contextMessages.length} messages for context out of ${messages.length} total`);

    const requestBody = {
        model: OPENAI_CONFIG.model,
        temperature: OPENAI_CONFIG.temperature,
        presence_penalty: OPENAI_CONFIG.presence_penalty,
        frequency_penalty: OPENAI_CONFIG.frequency_penalty,
        stream: true,
        messages: [...contextMessages, { role: 'user', content: promptToUse }],
        ...(shouldUseProxy && {
            apiUrl: 'https://api.openai.com/v1/chat/completions',
            headers: {
                'Content-Type': 'application/json'
            },
            // Include user info for token counting
            userId: user && user.uid,
            email: user && user.email
        })
    };

    const headers = shouldUseProxy ? {
            'Content-Type': 'application/json',
            'X-Service-Key': 'OPENAI_API_KEY', // Service key in separate header
            'Authorization': `Bearer ${await user.getIdToken()}` // Firebase token for auth
        } :
        // Direct mode - include auth header
        {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
        };

    // Debug request
    console.log('Sending request:', {
        endpoint,
        headers: {...headers, Authorization: '[REDACTED]' },
        body: requestBody
    });

    const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        const error = await response.text();
        throw new Error(`API request failed: ${error}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    const data = line.slice(6);
                    if (data === '[DONE]') continue;

                    try {
                        const parsed = JSON.parse(data);

                        // Handle OpenAI content
                        if (parsed.choices && parsed.choices[0] && parsed.choices[0].delta && parsed.choices[0].delta.content) {
                            yield parsed.choices[0].delta.content;
                        }
                        // Pass through token events from worker
                        else if (parsed.type === 'tokens') {
                            yield parsed;
                        }
                    } catch (e) {
                        if (!e.message.includes('Unterminated string')) {
                            console.warn('Failed to parse chunk:', e);
                        }
                    }
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}